import { createFileRoute, useNavigate, useRouterState, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { marked } from "marked";
import { Download, ArrowLeft, Send, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { QorumSeal } from "@/components/qorum/QorumSeal";
import { VerdictSignature } from "@/components/qorum/VerdictSignature";
import { NanaeHeader, NanaeFooter } from "@/components/qv3";
import { apiFetch, ApiError } from "@/lib/api";
import {
  logWaitStart,
  logWaitEnd,
  logWaitLongShown,
} from "@/lib/wait-instrumentation";
import { DialogueQorumSelectionLayer } from "@/components/Verdict/DialogueQorumSelectionLayer";
import { StabiloCoachmark } from "@/components/Verdict/StabiloCoachmark";
import { ShotokuEpigraph } from "@/components/qorum/ShotokuEpigraph";
import { NanaeDisclaimer } from "@/components/qorum/NanaeDisclaimer";
import type { VerdictClarity, ClarityReco } from "@/components/Verdict/VerdictClarity";
import type { Branch } from "@/lib/branches";
import DOMPurify from "dompurify";
import { BookPanel } from "@/components/book/BookPanel";
import { socialMeta } from "@/lib/seo-meta";

const HOJIN_GOLD = "#00E5FF";
const HOJIN_INK = "#000000";

interface VerdictApiResponse {
  branch?: Branch | string | null;
  verdict_clarity?: VerdictClarity | null;
  verdictClarity?: VerdictClarity | null;
}

const MEMO_SANITIZE_CONFIG = {
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "onchange", "onsubmit"],
  ADD_ATTR: ["target", "rel"],
};
const sanitizeMemoHtml = (html: string): string =>
  html ? DOMPurify.sanitize(html, MEMO_SANITIZE_CONFIG) : "";


export const Route = createFileRoute("/memo/$id")({
  head: ({ params }) => ({
    meta: [{ title: "Mémo stratégique — Hōjin" }, { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Mémo stratégique — Hōjin",
        description: "Mémo stratégique — Hōjin",
        url: `https://hojin.ai/memo/${params.id}`,
        image: "memo",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: `https://hojin.ai/memo/${params.id}` }],
  }),
  component: MemoPage,
});

interface MemoState {
  memo_markdown?: string;
  memo_html?: string;
  conversation_id?: string;
}

interface MemoApiResponse {
  memo_id: string;
  memo_markdown: string;
  memo_html?: string;
  conversation_id?: string;
}

const MD_LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const HREF_RE = /<a\s+([^>]*?)href="(https?:\/\/[^"]+)"([^>]*)>/gi;

function enhanceLinks(html: string): string {
  if (!html) return html;
  // 1) Convert any literal markdown links that slipped through to anchors.
  let out = html.replace(MD_LINK_RE, (_m, text: string, url: string) => {
    return `<a href="${url}">${text}</a>`;
  });
  // 2) Ensure every http(s) anchor opens in a new tab safely.
  out = out.replace(HREF_RE, (_m, pre: string, url: string, post: string) => {
    const attrs = `${pre} ${post}`.toLowerCase();
    const targetAttr = attrs.includes("target=") ? "" : ' target="_blank"';
    const relAttr = attrs.includes("rel=") ? "" : ' rel="noopener noreferrer"';
    return `<a ${pre.trim()} href="${url}" ${post.trim()}${targetAttr}${relAttr}>`;
  });
  return out;
}

function MemoPage() {
  const { id } = Route.useParams();
  return (
    <ProtectedRoute>
      {/* key={id} : force le remount lors d'un changement de mémo (ex. lien
          "voir le mémo d'origine" depuis une relance) — sinon les états
          markdown/html/conversationId restent figés sur l'ancien mémo. */}
      <MemoView key={id} />
    </ProtectedRoute>
  );
}


function MemoView() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const locationState = useRouterState({
    select: (s) => s.location.state as MemoState | undefined,
  });

  const [markdown, setMarkdown] = useState<string | null>(
    locationState?.memo_markdown ?? null,
  );
  const [html, setHtml] = useState<string | null>(locationState?.memo_html ?? null);
  const [conversationId, setConversationId] = useState<string | null>(
    locationState?.conversation_id ?? null,
  );
  const [loading, setLoading] = useState(!markdown);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verdictBranch, setVerdictBranch] = useState<Branch | null>(null);
  const [recosWithOwner, setRecosWithOwner] = useState<ClarityReco[]>([]);
  const [parentConversationId, setParentConversationId] = useState<string | null>(null);

  // Détecte si cette délibération est issue d'une relance (parent_conversation_id présent)
  // pour rassurer l'utilisateur que son mémo initial reste accessible.
  useEffect(() => {
    const cid = conversationId ?? id;
    if (!cid) return;
    let cancelled = false;
    apiFetch<{ conversation?: { parent_conversation_id?: string | null } }>(
      `/api/chat/${cid}/history`,
    )
      .then((res) => {
        if (cancelled) return;
        const pid = res?.conversation?.parent_conversation_id;
        if (typeof pid === "string" && pid.length > 0) setParentConversationId(pid);
      })
      .catch(() => {
        /* silencieux — cette info est un plus, pas critique */
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, id]);

  const articleRef = useRef<HTMLElement | null>(null);

  // Instrumentation : on mesure la durée réelle d'attente du mémo
  // (passage à pending=true jusqu'à l'arrivée du markdown ou d'une erreur).
  const waitOpenedRef = useRef(false);
  useEffect(() => {
    if (pending && !waitOpenedRef.current) {
      waitOpenedRef.current = true;
      logWaitStart("memo", { id });
    } else if (waitOpenedRef.current && (markdown || error)) {
      waitOpenedRef.current = false;
      logWaitEnd("memo", markdown ? "ready" : "error", { id });
    }
  }, [pending, markdown, error, id]);
  useEffect(() => {
    // Cas d'abandon (unmount pendant l'attente) : on clôt proprement.
    return () => {
      if (waitOpenedRef.current) {
        waitOpenedRef.current = false;
        logWaitEnd("memo", "abandoned", { id });
      }
    };
  }, [id]);

  // Polling mémo — respecte retry_after renvoyé par le 202.
  useEffect(() => {
    if (markdown) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const attempt = async () => {
      try {
        const res = await apiFetch<MemoApiResponse & { status?: string; retry_after?: number }>(
          `/api/upsell/secretaire/memo/${id}`,
        );
        if (cancelled) return;
        if (res && res.memo_markdown) {
          setMarkdown(res.memo_markdown);
          setHtml(res.memo_html ?? null);
          if (res.conversation_id) setConversationId(res.conversation_id);
          setPending(false);
          setLoading(false);
        } else {
          // 202 ou body vide — on continue à poller.
          const retry = (res as { retry_after?: number } | null)?.retry_after;
          const delay = typeof retry === "number" && retry > 0 ? retry * 1000 : 3000;
          setPending(true);
          setLoading(false);
          timer = setTimeout(attempt, delay);
        }
      } catch (e) {
        if (cancelled) return;
        const err = e as ApiError;
        if (err?.status === 202) {
          const retry = (err?.payload as { retry_after?: number } | null)?.retry_after;
          const delay = typeof retry === "number" && retry > 0 ? retry * 1000 : 3000;
          setPending(true);
          setLoading(false);
          timer = setTimeout(attempt, delay);
          return;
        }
        if (err?.status === 404) {
          setError("Mémo introuvable.");
        } else {
          setError("Impossible de charger le mémo.");
        }
        setLoading(false);
      }
    };

    attempt();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id, markdown]);

  // Récupération en parallèle du verdict pour : branch + recos avec owner.
  useEffect(() => {
    let cancelled = false;
    apiFetch<VerdictApiResponse>(`/api/verdict/${id}`)
      .then((res) => {
        if (cancelled || !res) return;
        const branch = (res.branch ?? null) as Branch | null;
        setVerdictBranch(branch);
        const clarity = res.verdict_clarity ?? res.verdictClarity ?? null;
        const recos = Array.isArray(clarity?.recommandations) ? clarity.recommandations : [];
        setRecosWithOwner(recos.filter((r) => r.owner?.cadre));
      })
      .catch(() => {
        // Silencieux — l'écran mémo fonctionne sans les boutons "Confier au Conseil".
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const showConfierPanel = verdictBranch !== "verif" && recosWithOwner.length > 0;


  const renderedHtml = sanitizeMemoHtml(
    enhanceLinks(
      html ??
        (markdown ? (marked.parse(markdown, { async: false }) as string) : ""),
    ),
  );

  const onDownload = () => {
    if (!renderedHtml) return;
    const win = window.open("", "_blank", "width=900,height=1200");
    if (!win) {
      alert("Veuillez autoriser les pop-ups pour télécharger le mémo en PDF.");
      return;
    }
    const filename = `hojin-memo-${id.slice(0, 8)}`;
    win.document.write(`<!doctype html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <title>${filename}</title>
  <style>
    @page { size: A4; margin: 18mm; }
    html, body { background: #ffffff; color: #000000; }
    body {
      font-family: "Fraunces", "Playfair Display", Georgia, serif;
      font-size: 12pt; line-height: 1.55; margin: 0; padding: 24px;
    }
    h1, h2, h3, h4 { font-family: "Fraunces", Georgia, serif; color: #000; page-break-after: avoid; }
    h1 { font-size: 22pt; margin: 0 0 16px; }
    h2 { font-size: 16pt; margin: 22px 0 10px; }
    h3 { font-size: 13pt; margin: 18px 0 8px; text-transform: uppercase; letter-spacing: 1px; color: #FF1744; }
    p, li { font-size: 11.5pt; }
    blockquote { border-left: 2px solid #FF1744; margin: 12px 0; padding: 4px 14px; color: #444; font-style: italic; }
    hr { border: none; border-top: 0.5px solid #ECECEC; margin: 16px 0; }
    a { color: #000; text-decoration: underline; }
    .footer { margin-top: 28px; font-size: 9pt; color: #888; text-align: center; letter-spacing: 2px; text-transform: uppercase; }
  </style>
</head>
<body>
  ${renderedHtml}
  <div class="footer">— Mémo par Hōjin —</div>
  <script>
    window.onload = function () { setTimeout(function(){ window.focus(); window.print(); }, 250); };
  </script>
</body>
</html>`);
    win.document.close();
  };

  const onBack = () => {
    if (conversationId) {
      navigate({ to: "/verdict/$id", params: { id: conversationId } });
    } else {
      navigate({ to: "/archives" });
    }
  };

  const btnGhost: React.CSSProperties = {
    background: "transparent",
    border: "0.5px solid var(--qv3-grey-100)",
    color: "var(--qv3-ink)",
    padding: "11px 18px",
    fontFamily: "var(--qv3-font-mono)",
    fontSize: 11,
    letterSpacing: 2,
    textTransform: "uppercase",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    transition: "border-color .2s, color .2s",
  };

  const btnGold: React.CSSProperties = {
    background: "var(--qv3-ink)",
    border: "1px solid var(--qv3-gold)",
    color: "var(--qv3-paper)",
    padding: "11px 18px",
    fontFamily: "var(--qv3-font-mono)",
    fontSize: 11,
    letterSpacing: 2,
    textTransform: "uppercase",
    cursor: markdown ? "pointer" : "not-allowed",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    transition: "background .2s, color .2s",
  };

  return (
    <div
      data-testid="memo-page"
      style={{
        background: "var(--qv3-paper)",
        minHeight: "100vh",
        paddingBottom: 80,
      }}
    >
      <NanaeHeader variant="minimal" title="Mémo stratégique" />

      <div
        data-testid="memo-supervision-bandeau"
        style={{
          maxWidth: 820,
          margin: "24px auto 0",
          padding: "10px 16px",
          textAlign: "center",
          fontFamily: "var(--qv3-font-mono)",
          fontSize: 11,
          letterSpacing: 3,
          textTransform: "uppercase",
          color: "var(--qv3-gold)",
          borderTop: "0.5px solid var(--qv3-gold-soft)",
          borderBottom: "0.5px solid var(--qv3-gold-soft)",
        }}
      >
        Sous supervision Hōjin
      </div>


      <div
        style={{
          maxWidth: 820,
          margin: "0 auto",
          padding: "24px 32px 0",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          onClick={onBack}
          style={btnGhost}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "var(--qv3-gold)";
            e.currentTarget.style.color = "var(--qv3-gold)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "var(--qv3-grey-100)";
            e.currentTarget.style.color = "var(--qv3-ink)";
          }}
        >
          <ArrowLeft size={14} /> Retour au verdict
        </button>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <style>{`
            @keyframes hojin-dash-pulse {
              0%, 100% { box-shadow: 0 0 0 0 color-mix(in oklab, var(--qv3-gold) 55%, transparent), 0 0 24px color-mix(in oklab, var(--qv3-gold) 25%, transparent); }
              50%      { box-shadow: 0 0 0 6px color-mix(in oklab, var(--qv3-gold) 0%, transparent),  0 0 36px color-mix(in oklab, var(--qv3-gold) 55%, transparent); }
            }
            @keyframes hojin-dash-shimmer {
              0%   { transform: translateX(-120%); }
              100% { transform: translateX(220%); }
            }
            .hojin-dash-cta {
              position: relative;
              overflow: hidden;
              background: var(--qv3-ink);
              border: 1px solid var(--qv3-gold);
              color: var(--qv3-paper);
              animation: hojin-dash-pulse 2.4s ease-in-out infinite;
            }
            .hojin-dash-cta::after {
              content: "";
              position: absolute;
              top: 0; left: 0; bottom: 0;
              width: 35%;
              background: linear-gradient(110deg, transparent 0%, color-mix(in oklab, var(--qv3-gold) 55%, transparent) 50%, transparent 100%);
              animation: hojin-dash-shimmer 2.8s ease-in-out infinite;
              pointer-events: none;
            }
            .hojin-dash-cta:hover {
              background: var(--qv3-gold);
              color: var(--qv3-ink);
            }
          `}</style>
          <button
            data-testid="download-memo-pdf"
            onClick={onDownload}
            disabled={!markdown}
            style={btnGhost}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--qv3-gold)";
              e.currentTarget.style.color = "var(--qv3-gold)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--qv3-grey-100)";
              e.currentTarget.style.color = "var(--qv3-ink)";
            }}
          >
            <Download size={14} /> Télécharger .pdf
          </button>
          <Link
            to="/dashboard/$id"
            params={{ id: conversationId ?? id }}
            data-testid="open-dashboard-view"
            className="hojin-dash-cta"
            style={{
              padding: "11px 20px",
              fontFamily: "var(--qv3-font-mono)",
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              transition: "background .2s, color .2s",
            }}
          >
            <LayoutDashboard size={14} /> Vue dashboard
          </Link>
        </div>
      </div>

      <div style={{ maxWidth: 820, margin: "0 auto", padding: "0 32px" }}>
        <BookPanel conversationId={conversationId ?? id} />
      </div>

      <article
        ref={articleRef}
        data-stabilo-target
        aria-label="Mémo stratégique"
        style={{
          maxWidth: 820,
          margin: "0 auto",
          padding: "48px 32px 0",
          position: "relative",
        }}
      >
        <div style={{ position: "absolute", top: 0, right: 0 }}>
          <QorumSeal idSuffix={`memo-${id}`} saisineNumber="MÉMO" />
        </div>

        <div
          style={{
            fontFamily: "var(--qv3-font-mono)",
            fontWeight: 500,
            fontSize: 12,
            letterSpacing: 3,
            textTransform: "uppercase",
            color: "var(--qv3-ink)",
            marginBottom: 12,
          }}
        >
          <span style={{ color: "var(--qv3-gold)", marginRight: 10 }}>§</span>
          Mémo stratégique
        </div>
        <h1
          style={{
            fontFamily: "var(--qv3-font-serif)",
            fontWeight: 300,
            fontSize: 44,
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
            color: "var(--qv3-ink)",
            margin: "0 0 32px 0",
          }}
        >
          Synthèse exécutive du Conseil
        </h1>

        {loading ? (
          <p
            role="status"
            style={{
              fontFamily: "var(--qv3-font-serif)",
              fontStyle: "italic",
              fontSize: 20,
              color: "var(--qv3-grey-500)",
              textAlign: "center",
              padding: "80px 0",
            }}
          >
            Chargement du mémo…
          </p>
        ) : pending ? (
          <MemoPendingLoader id={id} />
        ) : error ? (
          <p
            role="alert"
            style={{
              fontFamily: "var(--qv3-font-mono)",
              fontSize: 12,
              color: "var(--qv3-gold)",
              textAlign: "center",
              padding: "80px 0",
            }}
          >
            {error}
          </p>
        ) : (
          <>
            <style>{`
              @keyframes hojin-fade-in {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
              }
              .nanae-memo-prose > * {
                animation: hojin-fade-in 0.6s ease-out both;
              }
              .nanae-memo-prose > *:nth-child(1) { animation-delay: 0ms; }
              .nanae-memo-prose > *:nth-child(2) { animation-delay: 80ms; }
              .nanae-memo-prose > *:nth-child(3) { animation-delay: 160ms; }
              .nanae-memo-prose > *:nth-child(4) { animation-delay: 240ms; }
              .nanae-memo-prose > *:nth-child(5) { animation-delay: 320ms; }
              .nanae-memo-prose > *:nth-child(n+6) { animation-delay: 400ms; }
            `}</style>
            <div
              id="memo-content"
              data-testid="memo-content"
              className="nanae-memo-prose"
              dangerouslySetInnerHTML={{ __html: renderedHtml }}
            />
            {showConfierPanel ? (
              <ConfierAuConseilPanel
                conversationId={conversationId ?? id}
                recos={recosWithOwner}
              />
            ) : null}
          </>
        )}

        <VerdictSignature />


        <hr
          style={{
            border: "none",
            borderTop: "0.5px solid var(--qv3-grey-100)",
            margin: "24px 0",
          }}
        />
        <ShotokuEpigraph />
        <p
          data-testid="memo-signature"
          style={{
            fontFamily: "var(--qv3-font-mono)",
            fontWeight: 500,
            fontSize: 11,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: "var(--qv3-grey-500)",
            textAlign: "center",
            margin: "0 0 16px",
          }}
        >
          Mémo par Hōjin le {new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}.
        </p>
        <div style={{ textAlign: "center", paddingBottom: 32 }}>
          <Link
            to="/memo/$id/feedback"
            params={{ id }}
            style={{
              fontFamily: "var(--qv3-font-mono)",
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: "var(--qv3-gold)",
              textDecoration: "underline",
            }}
          >
            Donnez-nous votre avis
          </Link>
        </div>

        {parentConversationId && (
          <div
            role="note"
            aria-label="Votre analyse initiale reste disponible"
            style={{
              margin: "8px auto 24px",
              maxWidth: 640,
              padding: "14px 18px",
              background: "#000000",
              border: "1px solid rgba(0, 229, 255, 0.35)",
              color: "#F5EFE3",
              fontFamily: "var(--qv3-font-mono)",
              fontSize: 12,
              lineHeight: 1.6,
              letterSpacing: "0.04em",
              textAlign: "center",
            }}
          >
            Ce mémo affine votre question de suivi. Votre analyse initiale reste disponible —{" "}
            <Link
              to="/memo/$id"
              params={{ id: parentConversationId }}
              style={{
                color: "#00E5FF",
                textDecoration: "none",
                borderBottom: "1px solid rgba(0, 229, 255, 0.6)",
                paddingBottom: 1,
              }}
            >
              voir le mémo d'origine →
            </Link>
          </div>
        )}

        <NanaeDisclaimer />

      </article>

      {conversationId ? (
        <DialogueQorumSelectionLayer
          conversationId={conversationId}
          verdictText={markdown ?? ""}
          containerRef={articleRef}
        />
      ) : null}

      <StabiloCoachmark />


      <NanaeFooter variant="minimal" />


      <style>{`
        .nanae-memo-prose h1, .nanae-memo-prose h2 {
          font-family: var(--qv3-font-serif);
          color: var(--qv3-ink);
          margin: 40px 0 16px;
          line-height: 1.2;
          letter-spacing: -0.01em;
          font-weight: 400;
        }
        .nanae-memo-prose h1 { font-size: 32px; }
        .nanae-memo-prose h2 { font-size: 24px; }
        .nanae-memo-prose h3 {
          font-family: var(--qv3-font-mono);
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 3px;
          color: var(--qv3-gold);
          margin: 32px 0 14px;
          border-bottom: 0.5px solid var(--qv3-grey-100);
          padding-bottom: 10px;
        }
        .nanae-memo-prose p {
          font-family: var(--qv3-font-serif);
          font-size: 17px;
          line-height: 1.7;
          letter-spacing: -0.005em;
          color: var(--qv3-ink);
          margin: 0 0 20px;
        }
        .nanae-memo-prose blockquote {
          border-left: 2px solid var(--qv3-gold);
          background: var(--qv3-gold-tint);
          padding: 14px 20px;
          margin: 20px 0;
          font-family: var(--qv3-font-serif);
          font-style: italic;
          font-size: 17px;
          line-height: 1.7;
          color: var(--qv3-ink);
        }
        .nanae-memo-prose ul, .nanae-memo-prose ol {
          padding-left: 24px;
          margin: 0 0 20px;
        }
        .nanae-memo-prose li {
          font-family: var(--qv3-font-serif);
          font-size: 17px;
          line-height: 1.7;
          letter-spacing: -0.005em;
          color: var(--qv3-ink);
          margin-bottom: 8px;
        }
        .nanae-memo-prose ul li::marker { color: var(--qv3-gold); }
        .nanae-memo-prose a {
          color: #FF1744;
          text-decoration: none;
          text-underline-offset: 3px;
        }
        .nanae-memo-prose a:hover {
          color: #FF1744;
          text-decoration: underline;
          text-decoration-color: #FF1744;
        }
        .nanae-memo-prose strong { color: var(--qv3-ink); font-weight: 600; }
        .nanae-memo-prose em { font-style: italic; }
        .nanae-memo-prose hr { border: none; border-top: 0.5px solid var(--qv3-grey-100); margin: 32px 0; }
        .nanae-memo-prose code {
          font-family: var(--qv3-font-mono);
          font-size: 14px;
          background: var(--qv3-gold-tint);
          padding: 2px 6px;
          color: var(--qv3-ink);
        }
      `}</style>
    </div>
  );
}

/* ───────── Panel "Confier au Conseil" (coming soon) ───────── */
interface ConfierAuConseilPanelProps {
  conversationId: string;
  recos: ClarityReco[];
}

function ConfierAuConseilPanel({ conversationId, recos }: ConfierAuConseilPanelProps) {
  const handleConfier = (reco: ClarityReco) => {
    // Toast optimiste — affiché DANS TOUS LES CAS, indépendamment du réseau.
    toast(
      "Bientôt — le Conseil exécutera cette action pour vous (V2).",
      {
        description: reco.owner?.cadre
          ? `Confié à ${reco.owner.cadre}.`
          : undefined,
        duration: 4500,
      },
    );
    // POST en arrière-plan pour comptage. Toute erreur réseau est silencieuse.
    apiFetch(`/api/verdict/${conversationId}/action-interest`, {
      method: "POST",
      body: JSON.stringify({
        reco_id: reco.id,
        cadre: reco.owner?.cadre ?? null,
        action_label: reco.titre,
      }),
    }).catch(() => {
      // intentionnellement silencieux — UX coming-soon ne doit jamais casser.
    });
  };

  return (
    <section
      aria-label="Confier au Conseil"
      style={{
        marginTop: 56,
        paddingTop: 32,
        borderTop: `0.5px solid ${HOJIN_INK}33`,
      }}
    >
      <div
        style={{
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace",
          fontSize: 11,
          letterSpacing: 3,
          textTransform: "uppercase",
          color: HOJIN_INK,
          marginBottom: 6,
        }}
      >
        Confier au Conseil
      </div>

      <p
        style={{
          fontFamily: "var(--qv3-font-serif)",
          fontStyle: "italic",
          fontSize: 14,
          color: "var(--qv3-grey-500)",
          margin: "0 0 24px",
        }}
      >
        Bientôt, le Conseil exécutera ces actions pour vous.
      </p>
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        {recos.map((reco) => (
          <li
            key={reco.id}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 16,
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 20px",
              border: "0.5px solid var(--qv3-grey-100)",
              background: "rgba(0,0,0,0.015)",
            }}
          >
            <div style={{ flex: "1 1 320px", minWidth: 0 }}>
              <div
                style={{
                  fontFamily: "var(--qv3-font-serif)",
                  fontSize: 16,
                  fontWeight: 500,
                  color: "var(--qv3-ink)",
                  lineHeight: 1.35,
                  marginBottom: 4,
                }}
              >
                {reco.titre}
              </div>
              {reco.owner ? (
                <div
                  style={{
                    fontFamily: "var(--qv3-font-serif)",
                    fontSize: 13,
                    fontStyle: "italic",
                    color: "var(--qv3-grey-500)",
                  }}
                >
                  <span style={{ color: HOJIN_INK, fontStyle: "normal" }}>
                    {reco.owner.cadre}
                  </span>
                  {reco.owner.raison ? ` — ${reco.owner.raison}` : null}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => handleConfier(reco)}
              aria-label={`Confier au Conseil : ${reco.titre}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#E5E5E5",
                color: HOJIN_INK,
                border: `1px solid ${HOJIN_INK}`,
                padding: "10px 18px",
                fontFamily: "'JetBrains Mono', 'DM Mono', monospace",
                fontSize: 11,
                letterSpacing: 2,
                textTransform: "uppercase",
                cursor: "pointer",
                transition: "background .2s ease, color .2s ease",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = HOJIN_INK;
                e.currentTarget.style.color = "#E5E5E5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#E5E5E5";
                e.currentTarget.style.color = HOJIN_INK;
              }}
            >

              <Send size={12} />
              Confier au Conseil
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default MemoPage;

const PENDING_MESSAGES = [
  "Le Conseil rédige votre feuille de route…",
  "Synthèse des délibérations…",
  "Structuration du plan d'attaque…",
  "Mise en forme du mémo…",
  "Derniers ajustements…",
];
const PENDING_ETA_SECONDS = 85;
const PENDING_LONG_THRESHOLD = 95;

function MemoPendingLoader({ id }: { id: string }) {
  const [msgIdx, setMsgIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const msgTimer = setInterval(() => {
      setMsgIdx((i) => (i + 1) % PENDING_MESSAGES.length);
    }, 4500);
    const tickTimer = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => {
      clearInterval(msgTimer);
      clearInterval(tickTimer);
    };
  }, []);

  const progress = Math.min(98, (elapsed / PENDING_ETA_SECONDS) * 100);
  const longWait = elapsed >= PENDING_LONG_THRESHOLD;

  // Instrumentation : on journalise une seule fois l'affichage du message
  // "le Conseil prend plus de temps que prévu" pour calibrer le seuil.
  const longLoggedRef = useRef(false);
  useEffect(() => {
    if (longWait && !longLoggedRef.current) {
      longLoggedRef.current = true;
      logWaitLongShown("memo", elapsed, { id });
    }
  }, [longWait, elapsed, id]);

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="memo-pending"
      style={{
        textAlign: "center",
        padding: "64px 16px 80px",
        maxWidth: 520,
        margin: "0 auto",
      }}
    >
      <style>{`
        @keyframes hojin-pulse-dot {
          0%, 100% { transform: scale(1); opacity: 0.55; box-shadow: 0 0 0 0 ${HOJIN_GOLD}00; }
          50% { transform: scale(1.35); opacity: 1; box-shadow: 0 0 18px 4px ${HOJIN_GOLD}55; }
        }
        @keyframes hojin-orbit {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes hojin-msg-fade {
          0% { opacity: 0; transform: translateY(4px); }
          15%, 85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        @keyframes hojin-bar-shimmer {
          0% { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }
      `}</style>

      <div
        aria-hidden
        style={{
          position: "relative",
          width: 56,
          height: 56,
          margin: "0 auto 32px",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: `0.5px solid ${HOJIN_GOLD}44`,
            animation: "hojin-orbit 6s linear infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 8,
            height: 8,
            marginTop: -4,
            marginLeft: -4,
            borderRadius: "50%",
            background: HOJIN_GOLD,
            animation: "hojin-pulse-dot 1.6s ease-in-out infinite",
          }}
        />
      </div>

      <p
        key={msgIdx}
        style={{
          fontFamily: "var(--qv3-font-serif, 'Playfair Display', Georgia, serif)",
          fontStyle: "italic",
          fontSize: 22,
          lineHeight: 1.35,
          color: "var(--qv3-ink, #000)",
          margin: "0 0 28px",
          minHeight: 60,
          animation: "hojin-msg-fade 4.5s ease-in-out",
        }}
      >
        {PENDING_MESSAGES[msgIdx]}
      </p>

      <div
        aria-hidden
        style={{
          width: "100%",
          maxWidth: 320,
          height: 2,
          margin: "0 auto 14px",
          background: `${HOJIN_GOLD}1a`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${HOJIN_GOLD}66, ${HOJIN_GOLD}, ${HOJIN_GOLD}66)`,
            backgroundSize: "200px 100%",
            animation: "hojin-bar-shimmer 1.8s linear infinite",
            transition: "width 0.6s ease-out",
          }}
        />
      </div>

      <p
        style={{
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace",
          fontSize: 10,
          letterSpacing: 3,
          textTransform: "uppercase",
          color: "#000000",
          margin: 0,
        }}
      >
        {longWait
          ? "Le Conseil prend plus de temps que prévu — merci de patienter"
          : "Cela prend environ une minute"}
      </p>
    </div>
  );
}

